import React, { useEffect, useRef, useMemo } from "react";
import type { components } from "../../../api";
import { tint, usePalette, type Hex, type Palette } from "../../../theme";
import { categoryPaint, paintsOf, type Paints } from "../../../strata";
import { LUX_SCALE_MAX } from "../lighting";
import {
  elementText,
  flowSegments,
  flowText,
  type FlowView,
} from "../flows";
import { roleLetter } from "../catalog";
import { circuitColor, circuitIndexOf, type CircuitsView } from "../circuits";
import type { Drawable, ScopeView, Child } from "../queries";

// Canvas-scene constants -- the drawing's own rendering vocabulary,
// named once here, never scattered as literals mid-draw. Read from the
// palette in force: the canvas is a projection of the grammar, and the
// grammar has a light and a dark answer (docs/STRATA.md).
function sceneOf(p: Palette) {
  return { ground: p.canvasGrid, elementStroke: p.canvasStroke, popupBg: p.canvasPopup } as const;
}

/** What a pin is: a drawing's note, or one of the four outlet kinds the layout places. */
export type PinKind = "note" | "flow" | components["schemas"]["OutletKind"];

/** One pin on the canvas: where, what, whether the code put it there, and what it says. */
export interface Pin {
  x: number;
  y: number;
  z: number;
  kind: PinKind;
  /** The code's placement (a ring) as against the practice's. */
  code: boolean;
  gfci: boolean;
  text: string;
}

/** The marker colour per kind: the notes keep their blue; the outlets take the
 * categorical slots so no new hue is minted (paints law). */
function pinColorsOf(p: Palette, paints: Paints): Record<PinKind, string> {
  return {
    note: paints.action,
    flow: p.inkSecondary,
    receptacle: paints.verdict.complying,
    lighting: paints.absence,
    power: p.categorical[3],
    communications: p.categorical[1],
  };
}

// A pin counts as under the mouse within this many px.
const PIN_HOVER_RADIUS_PX = 10;

// A note's flag line rises this many px above its pin before the label sits.
const PIN_FLAG_OFFSET_PX = 14;

// The ground reference grid's dot radius scales with zoom, floored so it
// never disappears at a far zoom-out.
const GRID_DOT_SCALE_FACTOR = 0.015;
const GRID_DOT_MIN_RADIUS_PX = 0.75;

// The illuminance heatmap's ground cell size: 0.5 m in model units, floored
// in screen px so cells stay visible at a far zoom-out.
const GRID_CELL_SCALE_M = 0.5;
const GRID_CELL_MIN_PX = 2;

/** Draw one pin's marker: a dot for a note, a diamond for lighting, a triangle
 * for communications, a square for power, a dot for a receptacle -- and a
 * ring around any placement the code made. */
function drawPinMarker(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  pin: Pin,
  pinColor: Record<PinKind, string>,
  ringInk: Hex,
) {
  const r = 4;
  ctx.fillStyle = pinColor[pin.kind];
  ctx.strokeStyle = pinColor[pin.kind];
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  switch (pin.kind) {
    case "lighting":
      ctx.moveTo(x, y - r - 1);
      ctx.lineTo(x + r + 1, y);
      ctx.lineTo(x, y + r + 1);
      ctx.lineTo(x - r - 1, y);
      ctx.closePath();
      break;
    case "communications":
      ctx.moveTo(x, y - r - 1);
      ctx.lineTo(x + r + 1, y + r);
      ctx.lineTo(x - r - 1, y + r);
      ctx.closePath();
      break;
    case "power":
      ctx.rect(x - r, y - r, r * 2, r * 2);
      break;
    case "flow":
      // The edge's own marker is the line; its midpoint takes a small tick for the hover.
      ctx.rect(x - 2, y - 2, 4, 4);
      break;
    default:
      ctx.arc(x, y, r, 0, Math.PI * 2);
  }
  ctx.fill();
  if (pin.kind === "note") {
    // A note keeps its flag: the line from the pin up to where its text sits.
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x, y - PIN_FLAG_OFFSET_PX);
    ctx.stroke();
  }
  if (pin.code) {
    ctx.strokeStyle = ringInk;
    ctx.beginPath();
    ctx.arc(x, y, r + 3, 0, Math.PI * 2);
    ctx.stroke();
  }
}

interface CanvasVisualizerProps {
  elements: Drawable[];
  // The scope view containing the depth (counts, rows, whole) and children
  scopeView?: ScopeView | null;
  // Visibility control: a Set of origin_ids that are visible. Elements
  // not in this set are ghosted (low opacity) if ghostIds is set, or
  // not drawn at all otherwise.
  visibleIds?: Set<string>;
  // The illuminance heatmap: work-plane sample points (x, y, lux),
  // drawn as ground cells under the model. Sequential single-hue ramp
  // (amber, brightness = lux against the fixed LUX_SCALE_MAX reference).
  illuminancePoints?: [number, number, number][] | null;
  onElementMoved?: (originId: string, dx: number, dy: number) => void;
  onElementClick?: (element: Drawable | null) => void;
  // Navigate the scope ladder (depth counts): clicking a child descends
  onScopeChange?: (childOriginId: string) => void;
  // Elements with open redline findings: rendered with a red halo.
  violatingIds?: Set<string>;
  // The LIGHTING redline as geography: cells below floorLux paint in
  // redline red when the heatmap is on, and each failing room's worst
  // point is pinned regardless -- both derived from the open
  // min-illuminance findings (the rule's own required/measured facts).
  luxRedline?: { floorLux: number; worstPoints: [number, number][] } | null;
  // The pin lane, drawn OVER the elements (2026-09-13): the drawings' own
  // words where the drawing put them, and the outlets the engine proposes
  // -- a marker per kind, a ring for the code's, and the text only for the
  // pin under the mouse (a hundred labels at once is a wall, not a drawing).
  callouts?: Pin[] | null;
  // THE FLOW LANE (2026-09-13): the system as stored -- every coupling drawn
  // between its two elements, an arrowhead where the flow was oriented,
  // dashed where no direction reached, the stroke tinted by the potential
  // (bright at the hookup, dim at the loads), and each element's roles as
  // letters on its marker. Only the edges whose ends are on the canvas
  // draw, so the category filter isolates a system: electrical alone
  // shows the electrical flow alone.
  flow?: FlowView | null;
  // THE CIRCUITS LANE (2026-09-15): every element a drawn circuit claims painted in that circuit's
  // own hue, the rest faded, so a circuit reads as one thing across the plan.
  circuits?: CircuitsView | null;
  // The selected element: highlighted, and DRAGGABLE -- grabbing it
  // moves the ELEMENT (a plan-space edit op on drop), not the camera.
  selectedOriginId?: string | null;
  // Ghost, never vanish (APP_SPEC drilling): these elements render at
  // low opacity for context and are never pickable. The caller
  // decides membership (filter misses, peels); hidden mode simply
  // omits them from `elements` instead.
  ghostIds?: Set<string>;
  // Snap-to-interest: when this changes, the camera centers (and
  // zooms to a working scale) on the named element. The nonce lets
  // the same element be re-focused.
  focusTarget?: { originId: string; nonce: number } | null;
}

// A click that moved the mouse more than this (px) was a camera drag, not a
// pick attempt.
const CLICK_MOVE_THRESHOLD_PX = 5;
// Nearest pickable point must be within this many px of the click to count
// as a hit, rather than picking whatever's technically closest across the
// whole scene.
const PICK_RADIUS_PX = 12;
// Drag deltas snap to this world grid (meters): engineering moves are
// round numbers, and the drop posts exactly what the ghost showed.
const DRAG_SNAP_M = 0.005;

/**
 * Projects a 3D coordinate (x, y, z) into 2D canvas coordinates using azimuthal and elevation angles.
 */
function project3D(
  x: number,
  y: number,
  z: number,
  localScale: number,
  width: number,
  height: number,
  cx: number,
  cy: number,
  cz: number,
  angleTheta: number,
  anglePhi: number,
  panX: number,
  panY: number,
): { x: number; y: number } {
  const dx = x - cx;
  const dy = y - cy;
  const dz = z - cz;

  const cosT = Math.cos(angleTheta);
  const sinT = Math.sin(angleTheta);
  const rx1 = dx * cosT - dy * sinT;
  const ry1 = dx * sinT + dy * cosT;
  const rz1 = dz;

  const cosP = Math.cos(anglePhi);
  const sinP = Math.sin(anglePhi);
  const rx = rx1;
  const ry = ry1 * cosP - rz1 * sinP;

  return {
    x: width / 2 + rx * localScale + panX,
    y: height / 2 - ry * localScale + panY,
  };
}

// GeoJSON coordinate types: number[] for a single point, number[][] for line/ring,
// number[][][] for polygon with holes, number[][][][] for multi-polygon.
type GeoJSONCoordinates = number[] | number[][] | number[][][] | number[][][][];

// Extract the first vertex from nested GeoJSON coordinates.
// Recurses into arrays until finding a [number, number, ...] vertex.
const firstCoord = (c: GeoJSONCoordinates): [number, number, number] | null => {
  if (!Array.isArray(c)) return null;
  if (typeof c[0] === "number")
    return [c[0] as number, (c[1] as number) ?? 0, (c[2] as number) ?? 0];
  return firstCoord(c[0] as GeoJSONCoordinates);
};

// Extract all vertices from a GeoJSON Polygon: the exterior ring's coordinates.
// A Polygon has coordinates as [exterior, hole1, hole2, ...]; we draw only the exterior.
const polygonVertices = (coords: GeoJSONCoordinates): [number, number][] => {
  if (!Array.isArray(coords) || coords.length === 0) return [];
  const exterior = coords[0];
  if (!Array.isArray(exterior)) return [];
  return (exterior as number[][]).map(
    (c) => [c[0] ?? 0, c[1] ?? 0] as [number, number],
  );
};

/** A drawable outline for a child rung: its footprint as vertices and a label at the centroid. */
export interface ChildDrawable {
  originId: string;
  name: string;
  memberCount: number;
  vertices: [number, number][];
  centroid: [number, number];
}

/** Convert child rungs into drawable outlines: one per child with footprint vertices and centroid. */
export function childrenToDrawables(children: Child[]): ChildDrawable[] {
  return children
    .map((child) => {
      const vertices = polygonVertices(
        child.footprint.coordinates as GeoJSONCoordinates,
      );
      if (vertices.length === 0) return null;
      // Centroid: average of all vertices
      const cx = vertices.reduce((sum, v) => sum + v[0], 0) / vertices.length;
      const cy = vertices.reduce((sum, v) => sum + v[1], 0) / vertices.length;
      return {
        originId: child.origin_id,
        name: child.name || `Space ${child.origin_id.slice(-6)}`,
        memberCount: child.members,
        vertices,
        centroid: [cx, cy] as [number, number],
      };
    })
    .filter((d): d is ChildDrawable => d !== null);
}

/** Point-in-polygon detection (ray casting algorithm) for click handling on child footprints. */
export function pointInPolygon(
  point: [number, number],
  polygon: [number, number][],
): boolean {
  const [px, py] = point;
  let inside = false;

  let [previous] = polygon.slice(-1);
  for (const current of polygon) {
    const [xi, yi] = current;
    const [xj, yj] = previous;

    const intersect =
      yi > py !== yj > py && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
    previous = current;
  }

  return inside;
}

export const CanvasVisualizer: React.FC<CanvasVisualizerProps> = ({
  elements,
  scopeView,
  visibleIds,
  illuminancePoints,
  luxRedline,
  callouts,
  flow,
  circuits,
  onElementClick,
  onElementMoved,
  onScopeChange,
  violatingIds,
  selectedOriginId,
  ghostIds,
  focusTarget,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  // The paints in force: every colour the drawing puts down comes from
  // the palette, so a mode flip repaints on the next frame.
  const palette = usePalette();
  const paints = paintsOf(palette);
  const SCENE = sceneOf(palette);
  const PIN_COLOR = pinColorsOf(palette, paints);
  const containerRef = useRef<HTMLDivElement>(null);

  // Camera State (stored in refs to avoid re-rendering React on every mouse move)
  const cameraRef = useRef({
    angleTheta: -60 * (Math.PI / 180),
    anglePhi: 25 * (Math.PI / 180),
    zoomScale: 1.0,
    panX: 0,
    panY: 0,
  });

  // Schedule a redraw for the next frame, coalescing multiple state changes.
  const drawScheduleRef = useRef<number | null>(null);
  const scheduleDraw = () => {
    if (drawScheduleRef.current !== null) return; // Already scheduled
    drawScheduleRef.current = requestAnimationFrame(() => {
      drawScheduleRef.current = null;
      draw();
    });
  };

  // Children to draw at depth "counts": the rungs below the current scope.
  // Memoized to avoid recomputing vertices/centroids on every render.
  const childrenDrawables = useMemo(() => {
    if (scopeView?.depth === "counts") {
      return childrenToDrawables(scopeView.children);
    }
    return [];
  }, [scopeView]);

  // Cache per-element first coordinates. When the elements array identity
  // changes, this recomputes all first coordinates; otherwise they're stable.
  const elementCoords = useMemo(() => {
    const coords = new Map<string, [number, number, number]>();
    for (const el of elements) {
      const geom = el.geometry;
      if (geom?.coordinates) {
        const pt = firstCoord(geom.coordinates as GeoJSONCoordinates);
        if (pt) coords.set(el.origin_id, pt);
      }
    }
    return coords;
  }, [elements]);

  // Cache the auto-fit bounds: extract all first coordinates, compute percentiles.
  // Memoized by elementCoords so recompute only when coordinates actually change.
  const autofitBounds = useMemo(() => {
    const xs: number[] = [];
    const ys: number[] = [];
    const zs: number[] = [];
    for (const [, pt] of elementCoords) {
      xs.push(pt[0]);
      ys.push(pt[1]);
      zs.push(pt[2]);
    }
    // At the counts depth the children ARE the picture: their outlines set the fit (at z = 0).
    for (const child of childrenDrawables) {
      for (const [x, y] of child.vertices) {
        xs.push(x);
        ys.push(y);
        zs.push(0);
      }
    }
    if (xs.length === 0) return null;

    const percentile = (sorted: number[], p: number) => {
      const idx = (sorted.length - 1) * p;
      const lo = Math.floor(idx);
      const hi = Math.ceil(idx);
      const low = sorted.at(lo) ?? NaN;
      if (lo === hi) return low;
      return low + ((sorted.at(hi) ?? NaN) - low) * (idx - lo);
    };

    const sortedXs = [...xs].sort((a, b) => a - b);
    const sortedYs = [...ys].sort((a, b) => a - b);
    const sortedZs = [...zs].sort((a, b) => a - b);

    const minX = percentile(sortedXs, 0.05);
    const maxX = percentile(sortedXs, 0.95);
    const minY = percentile(sortedYs, 0.05);
    const maxY = percentile(sortedYs, 0.95);
    const minZ = percentile(sortedZs, 0.05);
    const maxZ = percentile(sortedZs, 0.95);

    return {
      minX,
      maxX,
      minY,
      maxY,
      minZ,
      cx: minX + (maxX - minX) / 2,
      cy: minY + (maxY - minY) / 2,
      cz: minZ + (maxZ - minZ) / 2,
    };
  }, [elementCoords, childrenDrawables]);

  // Interaction State
  const interactRef = useRef({
    isDragging: false,
    isPanning: false,
    lastMouseX: 0,
    lastMouseY: 0,
    lastTouchX: 0,
    lastTouchY: 0,
    lastTouchDist: 0,
    // Separate from lastMouseX/Y (which update every move) -- this stays
    // fixed at the mousedown position so mouseup can tell a real drag from
    // a click that jittered a couple pixels.
    downX: 0,
    downY: 0,
  });

  // The last draw's projection basis (percentile center + base
  // scale) -- what a focus jump needs to aim the camera.
  const drawParamsRef = useRef<{
    cx: number;
    cy: number;
    cz: number;
    baseScale: number;
  } | null>(null);

  // The widget drag: grabbing the SELECTED element moves the element,
  // not the camera. RAW world-space offset accumulates here; dx/dy
  // are the SNAPPED values (5 mm engineering grid -- precision is the
  // point of click-and-pull) that the ghost renders and the drop
  // posts. The ghost clears when server truth refetches.
  const elementDragRef = useRef<{
    originId: string;
    dx: number;
    dy: number;
    rawDx: number;
    rawDy: number;
  } | null>(null);
  // The last draw's projection scale -- needed to unproject screen
  // deltas into world meters while dragging.
  const projScaleRef = useRef(1);

  // Rebuilt every draw() call: one projected screen-space point per element,
  // for click hit-testing. A ref (not state) since it changes on every
  // camera move and must never trigger a re-render itself.
  const pickableRef = useRef<{ x: number; y: number; element: Drawable }[]>([]);

  // The pins' projected screen points, rebuilt every draw() for hover
  // hit-testing; and the one pin under the mouse, whose text is shown.
  const pinPointsRef = useRef<{ x: number; y: number; pin: Pin }[]>([]);
  const hoveredPinRef = useRef<Pin | null>(null);
  // EVERY ITEM SAYS WHAT IT IS ON HOVER (owner, 2026-09-13: "we used to be able to see all of the
  // items when we hovered over them"): an element under the cursor, where no pin is, gets its
  // name -- and its roles and potential when the flow lane is on. The pins keep precedence.
  const hoveredElementRef = useRef<Drawable | null>(null);

  // Color follows the entity's category, from the strata paints the
  // legend and overview share -- the canvas is a PROJECTION of the
  // grammar, not an exception (strict-grammar ruling 2026-08-06).
  const circuitIndex = circuitIndexOf(circuits);
  const getElementColor = (el: Drawable): string => {
    // Note: compliance_failure is not in the contract; elements with findings
    // are highlighted via the violatingIds prop instead.
    if (circuits) {
      const index = circuitIndex.get(el.origin_id);
      return index === undefined ? palette.inkFaint : circuitColor(index);
    }
    return categoryPaint(paints, el.category);
  };

  const draw = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const width = canvas.width / window.devicePixelRatio;
    const height = canvas.height / window.devicePixelRatio;
    ctx.clearRect(0, 0, width, height);

    if (
      (elements.length === 0 && childrenDrawables.length === 0) ||
      !autofitBounds
    )
      return;

    const { minX, maxX, minY, maxY, minZ, cx, cy, cz } = autofitBounds;

    const { angleTheta, anglePhi, zoomScale, panX, panY } = cameraRef.current;

    // Determine scale automatically to fit bounds
    const dx = maxX - cx;
    const dy = maxY - cy;
    // VERY rough heuristic for initial scale if bounds are large or small
    const maxRadius = Math.max(Math.sqrt(dx * dx + dy * dy), 0.1);
    const baseScale = Math.min(width, height) / (maxRadius * 2.5);
    const scale = baseScale * zoomScale;
    projScaleRef.current = scale;
    drawParamsRef.current = { cx, cy, cz, baseScale };

    const proj = (x: number, y: number, z: number) =>
      project3D(
        x,
        y,
        z,
        scale,
        width,
        height,
        cx,
        cy,
        cz,
        angleTheta,
        anglePhi,
        panX,
        panY,
      );

    // The 3D reference grid: a POUND-SIGN lattice, not a ground plane
    // or box faces -- lines run THROUGH the model volume along all three
    // axes at a fixed 5 m module (the model's units are meters; 5 m is
    // the drafting-conventional major for a building at this scale).
    const GRID_STEP = 5;
    const snapLo = (v: number) => Math.floor(v / GRID_STEP) * GRID_STEP;
    const snapHi = (v: number) => Math.ceil(v / GRID_STEP) * GRID_STEP;
    const bx0 = snapLo(minX),
      bx1 = snapHi(maxX);
    const by0 = snapLo(minY),
      by1 = snapHi(maxY);
    const bz0 = snapLo(minZ);
    ctx.lineWidth = 1;
    // The ground reference is a DOT grid on the floor plane -- one
    // dot per GRID_STEP meters at the model's z-floor. (The old
    // three-axis wireframe lattice read as walls of a box around the
    // building; the ground dots give the same spatial anchor without
    // the phantom planes.)
    ctx.fillStyle = SCENE.ground;
    const gridDotR = Math.max(scale * GRID_DOT_SCALE_FACTOR, GRID_DOT_MIN_RADIUS_PX);
    for (let gx = bx0; gx <= bx1; gx += GRID_STEP)
      for (let gy = by0; gy <= by1; gy += GRID_STEP) {
        const p = proj(gx, gy, bz0);
        ctx.beginPath();
        ctx.arc(p.x, p.y, gridDotR, 0, Math.PI * 2);
        ctx.fill();
      }

    // The illuminance heatmap: ground cells BEHIND the elements.
    // Sequential, one hue (dataviz law): lux ramps alpha+lightness on
    // amber against the ONE fixed reference (lighting.LUX_SCALE_MAX)
    // -- same color, same light level, in every project. Cells below
    // the open findings' floor flip to redline red: the advisory
    // becomes marked failing AREA, not just a dimmer amber.
    if (illuminancePoints && illuminancePoints.length > 0) {
      const cell = Math.max(scale * GRID_CELL_SCALE_M, GRID_CELL_MIN_PX);
      const floor = luxRedline?.floorLux;
      for (const [px, py, lux] of illuminancePoints) {
        const p = proj(px, py, minZ);
        if (floor !== undefined && lux < floor) {
          const deficit01 = 1 - lux / floor;
          ctx.fillStyle = tint(paints.verdict.violating, 0.12 + 0.45 * deficit01);
        } else {
          const t01 = Math.min(lux / LUX_SCALE_MAX, 1);
          ctx.fillStyle = tint(paints.absence, 0.06 + 0.5 * t01);
        }
        ctx.fillRect(p.x - cell / 2, p.y - cell / 2, cell, cell);
      }
    }

    // Draw elements
    const pickable: { x: number; y: number; element: Drawable }[] = [];

    const drag = elementDragRef.current;
    const shouldShow = (originId: string) =>
      !visibleIds || visibleIds.has(originId);

    elements.forEach((el) => {
      const geom = el.geometry;
      if (!geom) return;

      // Visibility: if visibleIds is set, skip elements not in it.
      // Ghost, never vanish: excluded elements keep their shape at
      // low opacity for context, and are never pick targets.
      const ghost = !shouldShow(el.origin_id);
      if (ghost && !ghostIds?.has(el.origin_id)) return; // Hidden mode: skip
      ctx.globalAlpha = ghost ? 0.12 : 1;

      const color = getElementColor(el);
      const violating = !ghost && (violatingIds?.has(el.origin_id) ?? false);
      const isSelected = el.origin_id === selectedOriginId;
      // The ghost: the in-flight drag offset applies to exactly the
      // dragged element until server truth replaces it.
      const ox = drag?.originId === el.origin_id ? drag.dx : 0;
      const oy = drag?.originId === el.origin_id ? drag.dy : 0;
      const projEl = (x: number, y: number, z: number) =>
        proj(x + ox, y + oy, z);

      if (geom.type === "Point") {
        const c = geom.coordinates as number[] | undefined;
        if (!c || c.length < 2) return;
        const pt = projEl(c[0], c[1], c[2] || 0);
        if (!ghost) pickable.push({ x: pt.x, y: pt.y, element: el });

        // Device dots scale WITH the building (px-per-meter), so
        // zooming out shrinks them into the fabric instead of leaving
        // a fixed-size dot soup; the glow ring and white outline are
        // zoom-earned detail, appearing only once dots are large
        // enough that they clarify rather than clutter.
        const dotR = Math.max(1.5, Math.min(10, scale * 0.18));

        if (violating) {
          // The redline halo.
          ctx.beginPath();
          ctx.arc(pt.x, pt.y, dotR + 6, 0, Math.PI * 2);
          ctx.strokeStyle = paints.verdict.violating;
          ctx.lineWidth = 2;
          ctx.stroke();
        }

        if (dotR >= 5) {
          ctx.beginPath();
          ctx.arc(pt.x, pt.y, dotR * 2, 0, Math.PI * 2);
          ctx.fillStyle = color + "44"; // 25% opacity
          ctx.fill();
        }

        ctx.beginPath();
        ctx.arc(pt.x, pt.y, dotR, 0, Math.PI * 2);
        ctx.fillStyle = color;
        ctx.fill();

        if (isSelected || dotR >= 3) {
          ctx.strokeStyle = isSelected ? paints.absence : SCENE.elementStroke;
          ctx.lineWidth = isSelected ? 2 : 1;
          ctx.stroke();
        }
      } else if (geom.type === "Polygon" || geom.type === "LineString") {
        // Simple polygon / linestring rendering
        const allCoords = geom.coordinates as
          number[][] | number[][][] | undefined;
        const coords =
          geom.type === "Polygon"
            ? (allCoords as number[][][] | undefined)?.[0]
            : (allCoords as number[][] | undefined);
        if (!coords || coords.length === 0) return;

        const path = () => {
          ctx.beginPath();
          const start = projEl(
            (coords[0] as number[])[0],
            (coords[0] as number[])[1],
            (coords[0] as number[])[2] || 0,
          );
          ctx.moveTo(start.x, start.y);
          let sumX = start.x,
            sumY = start.y;
          for (const vertex of coords.slice(1)) {
            const pt = projEl(vertex[0], vertex[1], vertex[2] || 0);
            ctx.lineTo(pt.x, pt.y);
            sumX += pt.x;
            sumY += pt.y;
          }
          return { cx: sumX / coords.length, cy: sumY / coords.length };
        };

        if (violating) {
          // The redline halo: a wide translucent red underlay stroke.
          path();
          if (geom.type === "Polygon") ctx.closePath();
          ctx.strokeStyle = tint(paints.verdict.violating, 0.45);
          ctx.lineWidth = 8;
          ctx.stroke();
        }

        const centroid = path();
        // Centroid of the projected points -- clicking anywhere near the
        // shape's visual center should hit it, not just its first vertex.
        if (!ghost)
          pickable.push({ x: centroid.cx, y: centroid.cy, element: el });

        if (geom.type === "Polygon") {
          ctx.closePath();
          ctx.fillStyle = color + "1f"; // 12% opacity
          ctx.fill();
        }

        ctx.strokeStyle = isSelected ? paints.absence : color;
        ctx.lineWidth = isSelected ? 3 : 2;
        ctx.stroke();
      }
    });

    // Draw children footprints at depth "counts": outlines and labels at centroids
    childScreenRef.current.clear();
    if (scopeView?.depth === "counts" && childrenDrawables.length > 0) {
      ctx.globalAlpha = 0.7;
      ctx.strokeStyle = palette.inkMuted;
      ctx.lineWidth = 2;
      ctx.font = "11px sans-serif";
      ctx.fillStyle = palette.inkMuted;

      for (const drawable of childrenDrawables) {
        // Draw the polygon outline
        const projected = drawable.vertices.map(([x, y]) => proj(x, y, minZ));
        childScreenRef.current.set(
          drawable.originId,
          projected.map((pt) => [pt.x, pt.y] as [number, number]),
        );
        if (projected.length > 0) {
          ctx.beginPath();
          ctx.moveTo(projected[0].x, projected[0].y);
          for (const pt of projected.slice(1)) {
            ctx.lineTo(pt.x, pt.y);
          }
          ctx.stroke();

          // Draw label at centroid: name and member count
          const centroidProj = proj(
            drawable.centroid[0],
            drawable.centroid[1],
            minZ,
          );
          const label = `${drawable.name} ×${drawable.memberCount}`;
          const metrics = ctx.measureText(label);
          const labelX = centroidProj.x - metrics.width / 2;
          const labelY = centroidProj.y + 4;
          ctx.fillText(label, labelX, labelY);
        }
      }
    }

    ctx.globalAlpha = 1;
    pickableRef.current = pickable;

    // Worst-point pins: the exact spot each open lighting finding
    // names -- drawn OVER the elements so the redline's geography is
    // never buried, heatmap on or off.
    if (luxRedline) {
      ctx.strokeStyle = paints.verdict.violating;
      ctx.lineWidth = 1.5;
      for (const [wx, wy] of luxRedline.worstPoints) {
        const p = proj(wx, wy, minZ);
        ctx.beginPath();
        ctx.arc(p.x, p.y, 7, 0, Math.PI * 2);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(p.x - 11, p.y);
        ctx.lineTo(p.x + 11, p.y);
        ctx.moveTo(p.x, p.y - 11);
        ctx.lineTo(p.x, p.y + 11);
        ctx.stroke();
      }
    }

    // THE FLOW LANE: each coupling as a segment between its elements' centroids, drawn under
    // the pins. Oriented edges carry an arrowhead at the receiving end; unoriented ones are
    // dashed, the residue drawn as residue. The stroke's alpha follows the mean potential, so
    // the hookup end of a system is bright and the loads dim; an unsolved component is plain.
    // Each edge's midpoint is a hover pin carrying the edge's own text. Roles ride the elements
    // as letters: S for a source, K for a sink, P for a process.
    const pinPoints: { x: number; y: number; pin: Pin }[] = [];
    const flowEdges = flow?.edges ?? [];
    if (flowEdges.length > 0) {
      const segments = flowSegments(
        flowEdges,
        elements.filter((el) => !(ghostIds?.has(el.origin_id) ?? false)),
      );
      ctx.save();
      for (const segment of segments) {
        const a = proj(segment.from.x, segment.from.y, segment.from.z);
        const b = proj(segment.to.x, segment.to.y, segment.to.z);
        const oriented = segment.edge.orientation !== "unoriented";
        ctx.globalAlpha = segment.phi == null ? 0.55 : 0.35 + 0.6 * segment.phi;
        ctx.strokeStyle = oriented ? paints.action : palette.inkFaint;
        ctx.fillStyle = ctx.strokeStyle;
        ctx.lineWidth = oriented ? 1.5 : 1;
        ctx.setLineDash(oriented ? [] : [4, 3]);
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.stroke();
        if (oriented) {
          const angle = Math.atan2(b.y - a.y, b.x - a.x);
          const head = 6;
          ctx.setLineDash([]);
          ctx.beginPath();
          ctx.moveTo(b.x, b.y);
          ctx.lineTo(
            b.x - head * Math.cos(angle - 0.5),
            b.y - head * Math.sin(angle - 0.5),
          );
          ctx.lineTo(
            b.x - head * Math.cos(angle + 0.5),
            b.y - head * Math.sin(angle + 0.5),
          );
          ctx.closePath();
          ctx.fill();
        }
        const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
        if (oriented) {
          // The chevron at the midpoint (Polly, 2026-09-15: "draw a > or < on the line so we can see
          // the flow direction"): the key's mark, pointing the way the flow goes.
          const angle = Math.atan2(b.y - a.y, b.x - a.x);
          const wing = 5;
          ctx.setLineDash([]);
          ctx.beginPath();
          ctx.moveTo(
            mid.x - wing * Math.cos(angle - 0.6),
            mid.y - wing * Math.sin(angle - 0.6),
          );
          ctx.lineTo(mid.x, mid.y);
          ctx.lineTo(
            mid.x - wing * Math.cos(angle + 0.6),
            mid.y - wing * Math.sin(angle + 0.6),
          );
          ctx.stroke();
        }
        pinPoints.push({
          x: mid.x,
          y: mid.y,
          pin: {
            x: 0,
            y: 0,
            z: 0,
            kind: "flow",
            code: false,
            gfci: false,
            text: flowText(segment.edge),
          },
        });
      }
      ctx.restore();
      ctx.font = "bold 10px sans-serif";
      ctx.fillStyle = palette.ink;
      for (const el of elements) {
        const roles = flow?.roles?.[el.origin_id];
        if (
          !roles ||
          roles.length === 0 ||
          (ghostIds?.has(el.origin_id) ?? false)
        )
          continue;
        const c = segments.find(
          (sg) => sg.edge.a === el.origin_id || sg.edge.b === el.origin_id,
        );
        if (!c) continue;
        const at = c.edge.a === el.origin_id ? c.from : c.to;
        const p = proj(at.x, at.y, at.z);
        ctx.fillText(
          roles.map(roleLetter).join(""),
          p.x + 5,
          p.y - 5,
        );
      }
    }

    // The pin lane: a marker per kind, a ring for the code's placements,
    // and the text for the hovered pin alone.
    if (callouts && callouts.length > 0) {
      for (const pin of callouts) {
        const p = proj(pin.x, pin.y, pin.z);
        pinPoints.push({ x: p.x, y: p.y, pin });
        drawPinMarker(ctx, p.x, p.y, pin, PIN_COLOR, palette.ink);
      }
    }
    {
      const hovered = hoveredPinRef.current;
      const at = hovered
        ? pinPoints.find(
            (pp) =>
              pp.pin === hovered ||
              (hovered.kind === "flow" &&
                pp.pin.kind === "flow" &&
                pp.pin.text === hovered.text),
          )
        : undefined;
      const label = (text: string, x: number, y: number) => {
        ctx.font = "11px sans-serif";
        const labelW = ctx.measureText(text).width;
        // Flip the box to the left when it would run off the canvas.
        const left = Math.max(
          4,
          x + 10 + labelW + 8 > width ? x - 10 - labelW - 8 : x + 6,
        );
        ctx.fillStyle = SCENE.popupBg;
        ctx.fillRect(left, y - 26, labelW + 8, 16);
        ctx.fillStyle = palette.ink;
        ctx.fillText(text, left + 4, y - PIN_FLAG_OFFSET_PX);
      };
      if (at) {
        label(at.pin.text, at.x, at.y);
      } else if (hoveredElementRef.current) {
        const el = hoveredElementRef.current;
        const under = pickableRef.current.find(
          (p) => p.element.origin_id === el.origin_id,
        );
        if (under) label(elementText(el, flow), under.x, under.y);
      }
    }
    pinPointsRef.current = pinPoints;

    // The pull is QUANTIFIED while it happens: a top-center readout of
    // the snapped world delta (the exact numbers the drop will post).
    const liveDrag = elementDragRef.current;
    if (liveDrag && (liveDrag.dx !== 0 || liveDrag.dy !== 0)) {
      const sign = (v: number) => `${v >= 0 ? "+" : ""}${v.toFixed(3)}`;
      const label = `Δx ${sign(liveDrag.dx)} m   Δy ${sign(liveDrag.dy)} m`;
      ctx.font = "12px monospace";
      const labelW = ctx.measureText(label).width;
      ctx.fillStyle = SCENE.popupBg;
      ctx.fillRect(width / 2 - labelW / 2 - 8, 10, labelW + 16, 22);
      ctx.fillStyle = paints.absence;
      ctx.fillText(label, width / 2 - labelW / 2, 25);
    }
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      const rect = parent.getBoundingClientRect();
      canvas.width = rect.width * window.devicePixelRatio;
      canvas.height = rect.height * window.devicePixelRatio;
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
      }
      scheduleDraw();
    };

    window.addEventListener("resize", resize);
    resize();

    return () => {
      window.removeEventListener("resize", resize);
    };
  }, []);

  useEffect(() => {
    // Re-draw whenever elements update -- and clear any drag ghost:
    // server truth has arrived (or the project changed) and the offset
    // must never double-apply on top of refetched geometry.
    elementDragRef.current = null;
    draw();
  }, [elements]);

  useEffect(() => {
    // The heatmap layer arrives (or clears) without the elements
    // changing -- the canvas is imperative, so the prop must repaint
    // it explicitly. Kept separate from the elements effect: lux data
    // landing mid-drag must not clear the drag ghost. The pins -- the
    // drawing's notes and the proposed outlets -- arrive the same way,
    // on their own queries after the elements, and repaint here too.
    // These are data-driven updates that should redraw immediately.
    // The palette is one of them: a mode flip is a repaint, not a
    // re-layout, and the canvas holds no subscription of its own.
    draw();
  }, [
    palette,
    illuminancePoints,
    callouts,
    flow,
    circuits,
    autofitBounds,
    elementCoords,
    selectedOriginId,
    violatingIds,
    ghostIds,
    luxRedline,
    scopeView,
  ]);

  // Snap-to-interest: aim the camera at the target's centroid. The
  // pan that centers a world point falls straight out of project3D
  // with pan=0 (panX = -rx*scale, panY = +ry*scale); zoom rises to a
  // working scale (~25 px/m) but never zooms OUT of a closer view.
  useEffect(() => {
    if (!focusTarget) return;
    const el = elements.find((e) => e.origin_id === focusTarget.originId);
    const dp = drawParamsRef.current;
    if (!el?.geometry || !dp) return;

    let sx = 0,
      sy = 0,
      sz = 0,
      n = 0;
    const walk = (node: unknown): void => {
      if (!Array.isArray(node)) return;
      if (typeof node[0] === "number") {
        sx += node[0] as number;
        sy += (node[1] as number) ?? 0;
        sz += (node[2] as number) ?? 0;
        n += 1;
        return;
      }
      for (const child of node) walk(child);
    };
    walk(el.geometry.coordinates);
    if (n === 0) return;

    const cam = cameraRef.current;
    cam.zoomScale = Math.max(cam.zoomScale, Math.min(25 / dp.baseScale, 60));
    const scale = dp.baseScale * cam.zoomScale;
    const dx = sx / n - dp.cx;
    const dy = sy / n - dp.cy;
    const dz = sz / n - dp.cz;
    const cosT = Math.cos(cam.angleTheta);
    const sinT = Math.sin(cam.angleTheta);
    const rx = dx * cosT - dy * sinT;
    const ry1 = dx * sinT + dy * cosT;
    const ry = ry1 * Math.cos(cam.anglePhi) - dz * Math.sin(cam.anglePhi);
    cam.panX = -rx * scale;
    cam.panY = ry * scale;
    draw();
  }, [focusTarget, autofitBounds, elementCoords]);

  // Screen-space delta -> world-space plan delta: the inverse of
  // project3D restricted to the horizontal plane (dz = 0). This is
  // what makes "drag the pipe half a meter" mean half a meter.
  const unprojectDelta = (
    screenDx: number,
    screenDy: number,
  ): { dx: number; dy: number } => {
    const { angleTheta, anglePhi } = cameraRef.current;
    const scale = projScaleRef.current || 1;
    const cosP = Math.cos(anglePhi);
    const rx1 = screenDx / scale;
    // Screen y grows downward; a near-horizontal camera (cosP -> 0)
    // cannot resolve plan-Y from screen-Y, so clamp the divisor.
    const ry1 =
      -screenDy /
      (scale * Math.max(Math.abs(cosP), 0.2) * Math.sign(cosP || 1));
    const cosT = Math.cos(angleTheta);
    const sinT = Math.sin(angleTheta);
    return { dx: rx1 * cosT + ry1 * sinT, dy: -rx1 * sinT + ry1 * cosT };
  };

  // Event Handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    interactRef.current.isDragging = true;
    interactRef.current.isPanning = e.button === 2 || e.shiftKey;
    interactRef.current.lastMouseX = e.clientX;
    interactRef.current.lastMouseY = e.clientY;
    interactRef.current.downX = e.clientX;
    interactRef.current.downY = e.clientY;

    // Grabbing the SELECTED element starts a widget drag (the element
    // moves, not the camera). Anything else keeps camera semantics.
    if (selectedOriginId && onElementMoved && !interactRef.current.isPanning) {
      const hit = pickAt(e.clientX, e.clientY);
      if (hit?.origin_id === selectedOriginId) {
        elementDragRef.current = {
          originId: selectedOriginId,
          dx: 0,
          dy: 0,
          rawDx: 0,
          rawDy: 0,
        };
      }
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    const inter = interactRef.current;
    const cam = cameraRef.current;

    if (inter.isDragging) {
      const deltaX = e.clientX - inter.lastMouseX;
      const deltaY = e.clientY - inter.lastMouseY;

      const drag = elementDragRef.current;
      if (drag) {
        const world = unprojectDelta(deltaX, deltaY);
        drag.rawDx += world.dx;
        drag.rawDy += world.dy;
        drag.dx = Math.round(drag.rawDx / DRAG_SNAP_M) * DRAG_SNAP_M;
        drag.dy = Math.round(drag.rawDy / DRAG_SNAP_M) * DRAG_SNAP_M;
      } else if (inter.isPanning) {
        cam.panX += deltaX;
        cam.panY += deltaY;
      } else {
        cam.angleTheta += deltaX * 0.007;
        cam.anglePhi = Math.max(
          -Math.PI / 3,
          Math.min(Math.PI / 3, cam.anglePhi + deltaY * 0.007),
        );
      }

      inter.lastMouseX = e.clientX;
      inter.lastMouseY = e.clientY;
      scheduleDraw();
      return;
    }

    // Not dragging: the pin under the mouse, if any, gets its text.
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    let nearest: Pin | null = null;
    let best = PIN_HOVER_RADIUS_PX;
    for (const pp of pinPointsRef.current) {
      const dist = Math.hypot(pp.x - x, pp.y - y);
      if (dist < best) {
        best = dist;
        nearest = pp.pin;
      }
    }
    const was = hoveredPinRef.current;
    let element: Drawable | null = null;
    if (!nearest) {
      let closest = PICK_RADIUS_PX;
      for (const p of pickableRef.current) {
        const dist = Math.hypot(p.x - x, p.y - y);
        if (dist < closest) {
          closest = dist;
          element = p.element;
        }
      }
    }
    const elementWas = hoveredElementRef.current;
    hoveredElementRef.current = element;
    if (
      element !== elementWas ||
      (nearest !== was &&
        !(
          nearest &&
          was &&
          nearest.kind === "flow" &&
          was.kind === "flow" &&
          nearest.text === was.text
        ))
    ) {
      hoveredPinRef.current = nearest;
      scheduleDraw();
    }
  };

  // Depth drilling (APP_SPEC): repeated clicks at the same spot cycle
  // down the stack of pickable elements under the cursor -- first the
  // nearest, then the one behind it, and around. No mode to enter;
  // drilling IS the click's second meaning.
  const pickCycleRef = useRef<{ x: number; y: number; cycle: number }>({
    x: -1,
    y: -1,
    cycle: 0,
  });

  // Each drawn child's outline in SCREEN space, as the last frame projected it: what a click tests.
  const childScreenRef = useRef<Map<string, [number, number][]>>(new Map());

  const pickAt = (clientX: number, clientY: number): Drawable | null => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;

    const candidates = pickableRef.current
      .map((p) => ({ p, dist: Math.hypot(p.x - x, p.y - y) }))
      .filter((c) => c.dist < PICK_RADIUS_PX)
      .sort((a, b) => a.dist - b.dist);
    if (candidates.length === 0) {
      pickCycleRef.current = { x: -1, y: -1, cycle: 0 };
      return null;
    }
    const last = pickCycleRef.current;
    const samePlace = Math.hypot(last.x - x, last.y - y) < PICK_RADIUS_PX;
    const cycle = samePlace ? (last.cycle + 1) % candidates.length : 0;
    pickCycleRef.current = { x, y, cycle };
    return candidates.at(cycle)?.p.element ?? null;
  };

  const handleMouseUp = (e: React.MouseEvent) => {
    const inter = interactRef.current;
    const moved = Math.hypot(e.clientX - inter.downX, e.clientY - inter.downY);
    inter.isDragging = false;
    inter.isPanning = false;

    const drag = elementDragRef.current;
    if (drag && moved > CLICK_MOVE_THRESHOLD_PX) {
      // The drop: one edit op carries the whole accumulated move. The
      // ghost stays visible until server truth refetches.
      onElementMoved?.(drag.originId, drag.dx, drag.dy);
      return;
    }
    elementDragRef.current = null;

    if (moved <= CLICK_MOVE_THRESHOLD_PX) {
      // Check if clicking inside a child polygon (at counts depth).
      if (
        scopeView?.depth === "counts" &&
        onScopeChange &&
        childScreenRef.current.size > 0
      ) {
        const rect = canvasRef.current?.getBoundingClientRect();
        const clickPoint: [number, number] = [
          e.clientX - (rect?.left ?? 0),
          e.clientY - (rect?.top ?? 0),
        ];
        for (const [childId, vertices] of childScreenRef.current) {
          if (pointInPolygon(clickPoint, vertices)) {
            onScopeChange(childId);
            return;
          }
        }
      }
      // Otherwise pick an element.
      onElementClick?.(pickAt(e.clientX, e.clientY));
    }
  };

  const handleMouseLeave = () => {
    interactRef.current.isDragging = false;
    interactRef.current.isPanning = false;
  };

  const handleWheel = (e: WheelEvent) => {
    e.preventDefault();
    const zoomIntensity = 0.08;
    const cam = cameraRef.current;
    if (e.deltaY < 0) {
      cam.zoomScale = Math.min(100.0, cam.zoomScale * (1.0 + zoomIntensity));
    } else {
      cam.zoomScale = Math.max(0.01, cam.zoomScale * (1.0 - zoomIntensity));
    }
    scheduleDraw();
  };

  // React's synthetic onWheel attaches passively, so preventDefault() there
  // is ignored and the browser logs an error on every scroll. Zooming must
  // stop the page from scrolling, which requires a native non-passive
  // listener. Routed through a ref because handleWheel closes over draw(),
  // which is rebuilt each render.
  const wheelHandlerRef = useRef(handleWheel);
  wheelHandlerRef.current = handleWheel;
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const onWheel = (e: WheelEvent) => wheelHandlerRef.current(e);
    canvas.addEventListener("wheel", onWheel, { passive: false });
    return () => canvas.removeEventListener("wheel", onWheel);
  }, []);

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
  };

  // Keyboard camera (audit 2026-08-06: the drawing must be reachable
  // without a mouse). Same semantics as the mouse: arrows orbit,
  // shift+arrows pan, +/- zoom at the wheel's factor. The canvas is
  // role="application": it owns its keys while focused.
  const handleKeyDown = (e: React.KeyboardEvent) => {
    const cam = cameraRef.current;
    const orbitStep = 0.06;
    const panStep = 24;
    const zoomIntensity = 0.08;
    let handled = true;
    if (e.key === "ArrowLeft") {
      if (e.shiftKey) cam.panX -= panStep;
      else cam.angleTheta -= orbitStep;
    } else if (e.key === "ArrowRight") {
      if (e.shiftKey) cam.panX += panStep;
      else cam.angleTheta += orbitStep;
    } else if (e.key === "ArrowUp") {
      if (e.shiftKey) cam.panY -= panStep;
      else cam.anglePhi = Math.min(Math.PI / 3, cam.anglePhi + orbitStep);
    } else if (e.key === "ArrowDown") {
      if (e.shiftKey) cam.panY += panStep;
      else cam.anglePhi = Math.max(-Math.PI / 3, cam.anglePhi - orbitStep);
    } else if (e.key === "+" || e.key === "=") {
      cam.zoomScale = Math.min(100.0, cam.zoomScale * (1.0 + zoomIntensity));
    } else if (e.key === "-" || e.key === "_") {
      cam.zoomScale = Math.max(0.01, cam.zoomScale * (1.0 - zoomIntensity));
    } else {
      handled = false;
    }
    if (handled) {
      e.preventDefault();
      scheduleDraw();
    }
  };

  // The drawing's accessible identity (audit 2026-08-06): what it
  // shows, said in words -- counts from the same props the pixels
  // come from, never a second source.
  const pinCount = luxRedline?.worstPoints.length ?? 0;
  const calloutCount = callouts?.length ?? 0;
  const drawingLabel =
    `Project drawing: ${elements.length.toLocaleString()} elements` +
    (violatingIds && violatingIds.size > 0
      ? `, ${violatingIds.size} with open redlines`
      : "") +
    (pinCount > 0 ? `, ${pinCount} low-light points pinned` : "") +
    (calloutCount > 0
      ? `, ${calloutCount} pins (drawing notes and proposed outlets)`
      : "") +
    ". Arrow keys orbit, shift plus arrows pan, plus and minus zoom.";

  return (
    <div
      ref={containerRef}
      style={{ width: "100%", height: "100%", position: "absolute", top: 0, left: 0, overflow: "hidden", cursor: "grab" }}
    >
      <canvas
        ref={canvasRef}
        style={{ display: "block", width: "100%", height: "100%", touchAction: "none" }}
        role="application"
        aria-label={drawingLabel}
        tabIndex={0}
        onKeyDown={handleKeyDown}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onContextMenu={handleContextMenu}
      />
    </div>
  );
};
